import { api, ApiError } from '../api.js';
import { bindSubmit, escape, setStatus, statusZone } from '../components/form.js';
import { navigate } from '../router.js';

/**
 * Bank account management.
 *
 * Add: user picks a bank from the gateway's directory + types an account
 * number; we POST to /wallet/banks which calls the gateway's resolve_account
 * API and stores the returned account name. If resolve fails the row is NOT
 * persisted, so list_banks only ever shows verified accounts.
 */
const STATUS_ID = 'banks-status';

export async function renderBanks(root) {
  root.innerHTML = `
    <div class="appbar">
      <a href="/wallet" data-link class="appbar-back" aria-label="Back">‹</a>
      <div class="appbar-title">Bank accounts</div>
    </div>
    <div class="pad" id="banks-page">
      <div class="skel"></div>
      <div class="skel"></div>
    </div>
  `;

  const host = document.getElementById('banks-page');
  let banks;
  let directory;
  try {
    [banks, directory] = await Promise.all([
      api('/wallet/banks'),
      api('/wallet/banks/directory'),
    ]);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) { navigate('/login'); return; }
    host.innerHTML = `<div class="card" style="color:var(--color-danger)">${escape(err.message)}</div>`;
    return;
  }

  host.innerHTML = `
    ${renderListCard(banks)}
    ${renderAddForm(directory)}
  `;

  wireDeletes(banks);
  wireAddForm();
}

function renderListCard(banks) {
  if (!banks.length) {
    return `<div class="card"><div class="muted">No bank accounts yet.</div></div>`;
  }
  return `
    <div class="card">
      <h2 class="card-title">Your accounts</h2>
      <ul class="txn-list">
        ${banks.map(b => `
          <li class="txn-row" data-bank-id="${escape(b.id)}">
            <div class="txn-row-main">
              <div class="txn-row-kind">${escape(b.bank_name)}</div>
              <div class="txn-row-sub">${escape(b.account_name)} · ${escape(b.account_number)}</div>
            </div>
            <div class="txn-row-side">
              <span class="status-pill ${b.verified ? 'ok' : 'warn'}">${b.verified ? 'verified' : 'unverified'}</span>
              <button type="button" class="link-button danger remove-bank" data-id="${escape(b.id)}">Remove</button>
            </div>
          </li>
        `).join('')}
      </ul>
    </div>
  `;
}

function renderAddForm(directory) {
  const options = directory
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(b => `<option value="${escape(b.code)}" data-name="${escape(b.name)}">${escape(b.name)}</option>`)
    .join('');
  return `
    <form id="add-bank-form" class="form card" novalidate>
      <h2 class="card-title">Add an account</h2>
      <label class="field">
        <span class="field-label">Bank</span>
        <select class="field-input" name="bank_code" required>
          <option value="" disabled selected>Pick a bank</option>
          ${options}
        </select>
      </label>
      <label class="field">
        <span class="field-label">Account number</span>
        <input class="field-input" name="account_number" type="text" required minlength="4" maxlength="20" pattern="\\d+" placeholder="10-digit NUBAN" />
      </label>
      ${statusZone(STATUS_ID)}
      <button type="submit" class="btn">Add and verify</button>
    </form>
  `;
}

function wireDeletes(_banks) {
  document.querySelectorAll('.remove-bank').forEach(btn => {
    btn.addEventListener('click', async () => {
      const id = btn.getAttribute('data-id');
      if (!confirm('Remove this bank account?')) return;
      btn.disabled = true;
      try {
        await api(`/wallet/banks/${id}`, { method: 'DELETE' });
        renderBanks(document.getElementById('route-shell'));
      } catch (err) {
        btn.disabled = false;
        setStatus(STATUS_ID, { message: err.message || 'Could not remove.', kind: 'error' });
      }
    });
  });
}

function wireAddForm() {
  const form = document.getElementById('add-bank-form');
  bindSubmit(form, async (data) => {
    setStatus(STATUS_ID, { message: 'Verifying account…', kind: 'info' });
    const select = form.querySelector('select[name="bank_code"]');
    const bankName = select.options[select.selectedIndex]?.dataset.name || '';
    try {
      await api('/wallet/banks', {
        method: 'POST',
        body: {
          bank_code: data.bank_code,
          bank_name: bankName,
          account_number: data.account_number,
        },
      });
      setStatus(STATUS_ID, { message: 'Verified.', kind: 'success' });
      renderBanks(document.getElementById('route-shell'));
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not add account.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
    }
  });
}
